/**
 * Headcount editor (W.2) — the operator declares a point-in-time accounting
 * snapshot for a community (evacuated / remaining / accounted-for / unreachable),
 * the data behind ICS-209 block 31. POSTs to /api/communities/:id/headcounts
 * (audited `declare_headcount`, W.1). `as_of` defaults to now but is operator-
 * editable so a reported "as of 0600" figure can be backfilled (W.1 capability).
 *
 * Modal pattern mirrors AssistanceRequestForm — Esc/backdrop close, validate
 * before submit, toast on success.
 */
import { useEffect, useState } from 'react';
import type { CommunityHeadcount, HeadcountSource } from '@tsr/shared-types';
import { createCommunityHeadcount } from './api';
import { getSessionId } from './lib/operatorActions';
import { showToast } from './lib/toast';
import {
  EVAC_ACCOUNTING_AWAITING_AIRLIFT_LABEL,
  EVAC_ACCOUNTING_EVACUATED_LABEL,
  EVAC_ACCOUNTING_REMAINING_LABEL,
  EVAC_ACCOUNTING_UNACCOUNTED_LABEL,
} from './incidentVocabulary';

const SOURCES: { value: HeadcountSource; label: string }[] = [
  { value: 'operator', label: 'Operator' },
  { value: 'community_official', label: 'Community official' },
  { value: 'mutual_aid', label: 'Mutual aid' },
  { value: 'agency_report', label: 'Agency report' },
  { value: 'estimate', label: 'Estimate' },
];

/** Local datetime-input default — now, in the operator's zone, no seconds. */
function nowLocalInput(): string {
  const d = new Date(Date.now() - new Date().getTimezoneOffset() * 60_000);
  return d.toISOString().slice(0, 16);
}

interface CountField {
  key: 'evacuated' | 'remaining' | 'accounted_for' | 'unreachable';
  label: string;
}
// #462 — the field labels route through the incidentVocabulary doctrine gate and match the
// accounting panel's terms (one field, one name across both surfaces): "Remaining in
// community" (not "Remaining"), "Not yet accounted for" (not "Unreachable").
const COUNT_FIELDS: CountField[] = [
  { key: 'evacuated', label: EVAC_ACCOUNTING_EVACUATED_LABEL },
  { key: 'remaining', label: EVAC_ACCOUNTING_REMAINING_LABEL },
  { key: 'accounted_for', label: 'Accounted for' },
  { key: 'unreachable', label: EVAC_ACCOUNTING_UNACCOUNTED_LABEL },
];

export function HeadcountEditorForm({
  community,
  incidentId,
  defaultSource = 'operator',
  onClose,
  onSubmitted,
}: {
  community: { id: string; name: string };
  /** Pre-link to the community's active incident, if one is known. */
  incidentId?: string | null;
  /** #462 — the pre-selected source. The evacuation-accounting section (recording on a
   *  community's behalf) passes 'community_official' — the count is the community's own
   *  (FN-dignity). Defaults to 'operator' for the general community-panel entry. */
  defaultSource?: HeadcountSource;
  onClose: () => void;
  onSubmitted: (headcount: CommunityHeadcount) => void;
}): JSX.Element {
  const [asOf, setAsOf] = useState<string>(nowLocalInput);
  const [counts, setCounts] = useState<Record<CountField['key'], string>>({
    evacuated: '0',
    remaining: '0',
    accounted_for: '0',
    unreachable: '0',
  });
  const [source, setSource] = useState<HeadcountSource>(defaultSource);
  // #462 — awaiting-flight is a NULLABLE split of `remaining`: empty = "not broken out /
  // unknown" (≠ 0). Kept as its own string field (not in the required 4-up) so an empty
  // input stays unknown rather than being coerced to 0.
  const [airlift, setAirlift] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const parseCount = (v: string): number | null => {
    if (v.trim() === '') return 0;
    const n = Number(v);
    return Number.isInteger(n) && n >= 0 ? n : null;
  };

  const submit = (): void => {
    if (busy) return;
    const parsed: Record<string, number> = {};
    for (const f of COUNT_FIELDS) {
      const n = parseCount(counts[f.key]);
      if (n === null) {
        showToast(`${f.label} must be a non-negative whole number.`, { variant: 'error' });
        return;
      }
      parsed[f.key] = n;
    }
    if (Object.values(parsed).every((n) => n === 0)) {
      showToast('Enter at least one non-zero count.', { variant: 'error' });
      return;
    }
    // Awaiting-flight: empty → null (unknown, not 0); otherwise a non-negative whole number
    // that cannot exceed the remaining-in-community count (it is a SPLIT of remaining).
    let awaitingAirlift: number | null = null;
    if (airlift.trim() !== '') {
      const a = Number(airlift);
      if (!Number.isInteger(a) || a < 0) {
        showToast('Awaiting flight must be a non-negative whole number.', { variant: 'error' });
        return;
      }
      if (a > parsed.remaining!) {
        showToast('Awaiting flight can’t exceed the remaining-in-community count.', {
          variant: 'error',
        });
        return;
      }
      awaitingAirlift = a;
    }
    // datetime-local → ISO 8601 (the API requires a zoned timestamp). Empty → now.
    let asOfIso: string | undefined;
    if (asOf.trim()) {
      const d = new Date(asOf);
      if (Number.isNaN(d.getTime())) {
        showToast('As-of time is invalid.', { variant: 'error' });
        return;
      }
      asOfIso = d.toISOString();
    }

    setBusy(true);
    void createCommunityHeadcount(community.id, {
      incident_id: incidentId ?? undefined,
      as_of: asOfIso,
      evacuated: parsed.evacuated!,
      remaining: parsed.remaining!,
      accounted_for: parsed.accounted_for!,
      unreachable: parsed.unreachable!,
      awaiting_airlift: awaitingAirlift,
      source,
      notes: notes.trim() || undefined,
      session_id: getSessionId(),
    })
      .then((headcount) => {
        showToast('Headcount recorded.', { variant: 'success' });
        onSubmitted(headcount);
      })
      .catch(() => showToast('Could not record the headcount. Please retry.', { variant: 'error' }))
      .finally(() => setBusy(false));
  };

  const inputCls =
    'w-full text-xs bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-slate-100 placeholder-slate-500';

  return (
    <div
      data-testid="headcount-form-backdrop"
      onClick={onClose}
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 p-6"
    >
      <div
        data-testid="headcount-form-modal"
        onClick={(e) => e.stopPropagation()}
        className="flex flex-col w-full max-w-md max-h-[88vh] rounded-lg border border-rose-700/60 bg-slate-900 text-slate-100 shadow-2xl"
      >
        <header className="flex items-center justify-between px-5 py-3 border-b border-slate-700">
          <h2 className="text-sm font-semibold tracking-tight">
            Record headcount — <span className="text-rose-300">{community.name}</span>
          </h2>
          <button
            type="button"
            data-testid="headcount-form-close"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-100 text-lg leading-none px-1"
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-3">
          <label className="block text-[11px] text-slate-400 space-y-1">
            <span>As of</span>
            <input
              type="datetime-local"
              data-testid="headcount-as-of"
              value={asOf}
              onChange={(e) => setAsOf(e.target.value)}
              className={inputCls}
            />
          </label>

          <div className="grid grid-cols-2 gap-2">
            {COUNT_FIELDS.map((f) => (
              <label key={f.key} className="text-[11px] text-slate-400 space-y-1">
                <span>{f.label}</span>
                <input
                  type="number"
                  min={0}
                  step={1}
                  data-testid={`headcount-${f.key}`}
                  value={counts[f.key]}
                  onChange={(e) => setCounts((p) => ({ ...p, [f.key]: e.target.value }))}
                  className={inputCls}
                />
              </label>
            ))}
          </div>

          {/* #462 — awaiting-flight: a NULLABLE split of "remaining". Empty = not broken
              out / unknown (≠ 0). The northern air-evac number the FN air-evac seat reads. */}
          <label className="block text-[11px] text-slate-400 space-y-1">
            <span>
              {EVAC_ACCOUNTING_AWAITING_AIRLIFT_LABEL}{' '}
              <span className="text-slate-500">(optional — of those remaining)</span>
            </span>
            <input
              type="number"
              min={0}
              step={1}
              data-testid="headcount-awaiting-airlift"
              value={airlift}
              onChange={(e) => setAirlift(e.target.value)}
              placeholder="unknown"
              className={inputCls}
            />
          </label>

          <label className="block text-[11px] text-slate-400 space-y-1">
            <span>Source</span>
            <select
              data-testid="headcount-source"
              value={source}
              onChange={(e) => setSource(e.target.value as HeadcountSource)}
              className={inputCls}
            >
              {SOURCES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-[11px] text-slate-400 space-y-1">
            <span>Notes (optional)</span>
            <textarea
              data-testid="headcount-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Context for this snapshot."
              className={`${inputCls} resize-none`}
            />
          </label>
        </div>

        <footer className="flex items-center gap-2 px-5 py-3 border-t border-slate-700">
          <button
            type="button"
            data-testid="headcount-submit"
            disabled={busy}
            onClick={submit}
            className="text-xs py-1.5 px-3 rounded bg-rose-600/90 hover:bg-rose-600 border border-rose-500 text-white font-medium disabled:opacity-50 disabled:cursor-wait"
          >
            {busy ? 'Recording…' : 'Record headcount'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto text-xs py-1.5 px-3 rounded bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300"
          >
            Cancel
          </button>
        </footer>
      </div>
    </div>
  );
}
